<template>
  <div :class="$forms.stepperContainer">
    <app-loader :show-loader="getLoadingStatus" />

    <app-delete-dialog :value="deleteDialog"
                       @delete="deleteAssetItem(deleteId)"
                       @close="closeDialog">
      Are you sure you want to delete this asset?
    </app-delete-dialog>

    <div :class="$forms.formMainContainer">
      <h2>Other Assets</h2>
      <p>
        Add, Edit or Delete any other assets you have.
      </p>

      <form>
        <div>
          <app-data-table
            table-title="List of Other Assets"
            add-button="+ Add Asset"
            :headers="otherAssetsHeaders"
            :items="getOtherAssets"
            @addNew="addNewAsset"
            empty-text="There is currently no other assets added yet.
                        Please click on ‘+Add Asset’ to start adding new asset."
          >
            <template v-slot:tableRows>
              <tr v-for="item in getOtherAssets"
                  :key="item.id"
                  :class="$forms.tableDataRow">
                <td :class="$forms.description"
                    @click="showDetails(item.id)">
                  {{ item.otherAssetType | displayAsset }}
                </td>
                <td>{{ item.monthlyPayment | currency }}</td>
                <td>{{ item.yearPurchased }}</td>
                <td>{{ item.finalPaymentMonth | displayMonth }}</td>
                <td>
                  <template v-if="item.finalPaymentYear > 0">
                    {{ item.finalPaymentYear }}
                  </template>
                </td>
                <td :class="$forms.tableRowButtons">
                  <button :class="$forms.tableBtn"
                          @click.prevent="showDialog(item.id)">
                    <app-svg-icon name="ic_delete"
                                  :class="$forms.tableIconDelete" />
                  </button>
                </td>
                <td :class="$forms.tableRowButtons">
                  <button :class="$forms.tableBtn"
                          @click.prevent="editAsset(item.id)">
                    <app-svg-icon name="ic_edit@3x"
                                  :class="$forms.tableIconEdit" />
                  </button>
                </td>
              </tr>
            </template>
          </app-data-table>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import currency from 'currency.js';
import { mapGetters, mapActions } from 'vuex';
import formsMixin from '@/app/core/mixins/forms-mixin';
import { transformMonthToString } from '@/utils/transforms';
import { MONTHS, OTHER_ASSET_TYPES } from '@/constants';

export default {
  data() {
    return {
      step: 5,
      isInvalid: false,
      otherAssetsHeaders: [
        'Description',
        'Monthly Payment',
        'Year Purchased',
        'Final Payment Month',
        'Final Payment Year',
      ],
      deleteDialog: false,
      deleteId: 0,
    };
  },
  methods: {
    ...mapActions([
      'deleteAsset',
      'assetItemRequest',
      'householdRequest',
      'refreshRequest',
    ]),
    displayAddAssetSidebar() {
      this.$emit('displayAssetSidebar');
    },
    showDialog(id) {
      this.deleteDialog = true;
      this.deleteId = id;
    },
    closeDialog() {
      this.deleteDialog = false;
      this.deleteId = 0;
    },
    /* Delete vehicle by click at the Delete button in the dialog */
    deleteAssetItem(id) {
      this.deleteAsset({
        id,
        userId: this.getUserId,
      }).then(() => this.closeDialog());
    },
    /* Here we don't need to send any data - we
    just emit the confirmation to go to the next step */
    sendFormData() {
      this.householdRequest(this.getUserId)
        .then(() => {
          this.$emit('nextStepDone', this.currentStep);
        })
        .catch((err) => {
          if (err.response.status === 401) {
            this.refreshRequest()
              .then(() => {
                this.householdRequest(this.getUserId)
                  .then(() => {
                    this.$emit('nextStepDone', this.currentStep);
                  });
              });
          }
        });
    },
    editAsset(id) {
      this.assetItemRequest({
        itemId: id,
        userId: this.getUserId,
      }).then(() => {
        this.$emit('editAsset', this.getItemDetails, id);
      });
    },
    /* Show Details sidebar */
    showDetails(id) {
      this.assetItemRequest({
        itemId: id,
        userId: this.getUserId,
      }).then(() => {
        this.$emit('showDetails', this.getItemDetails);
      });
    },
    /* Open AddAsset sidebar */
    addNewAsset() {
      this.displayAddAssetSidebar();
    },
  },
  computed: {
    ...mapGetters([
      'getUserId',
      'getItemDetails',
      'getOtherAssets',
      'getOtherAssetsLoadingStatus',
    ]),
    getMonths() {
      return MONTHS;
    },
    getOtherAssetsTypes() {
      return OTHER_ASSET_TYPES.map(type => type.textValue);
    },
    getLoadingStatus() {
      return this.getOtherAssetsLoadingStatus === 'loading';
    },
    getInvalid() {
      return this.isInvalid;
    },
  },
  mixins: [
    formsMixin,
  ],
  directives: {
    currency,
  },
  filters: {
    displayAsset(value) {
      return OTHER_ASSET_TYPES.find(type => type.code === value).textValue;
    },
    displayMonth(value) {
      return transformMonthToString(value);
    },
  },
};
</script>

<style src="@/styles/forms.scss" lang="scss" module="$forms"></style>
